import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	createApiToken,
	dismissApiTokenError,
	loadApiTokens,
	revokeApiToken,
} from '../../../redux/actions/settings.js';
import Button from '../../components/Button.jsx';
import Card from '../../components/Card.jsx';
import CircleLoader from '../../components/CircleLoader.jsx';
import TextInput from '../../elements/TextInput.jsx';

/**
 * Personal API tokens: create, list and revoke.
 *
 * A new token's plaintext is held in this component's state and nowhere else -- not the store, not
 * localStorage -- and goes when the box showing it is dismissed or the page is left. The server only
 * keeps a hash, so this is the one chance to copy it.
 */
export default function ApiTokenSettings() {
	const dispatch = useDispatch();
	const apiTokens = useSelector((state) => state.settings.apiTokens);
	const creating = useSelector((state) => state.settings.creatingApiToken);
	const revokingId = useSelector((state) => state.settings.revokingApiTokenId);
	const error = useSelector((state) => state.settings.apiTokensError);

	const [name, updateName] = useState('');
	const [newToken, updateNewToken] = useState(null);
	const [copyStatus, updateCopyStatus] = useState(null);
	const tokenInput = useRef(null);

	useEffect(() => {
		dispatch(loadApiTokens());
	}, []);

	const canCreate = name.trim() !== '' && !creating;

	const onCreate = (event) => {
		event.preventDefault();
		if(!canCreate) {
			return;
		}
		dispatch(createApiToken(name.trim()))
			.then((token) => {
				if(token) {
					updateNewToken(token);
					updateCopyStatus(null);
					updateName('');
				}
			});
	};

	const onRevoke = (token) => {
		const confirmed = window.confirm(
			`Revoke "${token.name}"? Anything still using it will stop working immediately.`
		);
		if(!confirmed) {
			return;
		}
		dispatch(revokeApiToken(token.id));
	};

	const onCopy = () => {
		copyText(newToken, tokenInput.current)
			.then((copied) => {
				updateCopyStatus(copied ? 'copied' : 'failed');
			});
	};

	const onDismiss = () => {
		updateNewToken(null);
		updateCopyStatus(null);
	};

	return (
		<div className='api-token-settings'>
			<Card>
				<h5>API tokens</h5>
				<p className='grey-text'>
					A token lets a script act as you through the API. Send it with each request as
					an <code>Authorization: Bearer &lt;token&gt;</code> header to <code>/api/v1/...</code>.
					Treat it like a password: anyone holding it can do what you can. Revoke a token as
					soon as you stop using it or think it has leaked.
				</p>

				{!!error &&
				<div className='card-panel red lighten-4'>
					<span>{error}</span>{' '}
					<Button small flat onClick={() => dispatch(dismissApiTokenError())}>Dismiss</Button>
				</div>
				}

				{!!newToken &&
				<div className='card-panel yellow lighten-4 api-token-new'>
					<strong>Your new token</strong>
					<p>
						Copy it now. It will not be shown again; if you lose it, revoke it and create
						another.
					</p>
					<input
						ref={tokenInput}
						type='text'
						readOnly
						value={newToken}
						className='api-token-plaintext'
						onFocus={(event) => event.target.select()}
						style={{ fontFamily: 'monospace' }}
					/>
					<Button small onClick={onCopy}>Copy</Button>{' '}
					<Button small flat onClick={onDismiss}>Done</Button>
					{copyStatus === 'copied' && <span className='green-text'> Copied.</span>}
					{copyStatus === 'failed' &&
					<span className='red-text'> Could not copy; select the token and copy it yourself.</span>
					}
				</div>
				}

				<form onSubmit={onCreate}>
					<div className='input-field'>
						<TextInput
							label='Token name'
							value={name}
							disabled={creating}
							onChange={(event) => updateName(event.target.value)}
						/>
					</div>
					<Button type='submit' disabled={!canCreate}>
						{creating ? 'Creating…' : 'Create'}
					</Button>
				</form>
			</Card>

			<Card>
				<h5>Active tokens</h5>
				{apiTokens === null && <CircleLoader size='small' />}
				{apiTokens !== null && apiTokens.length === 0 &&
				<p className='grey-text'>No tokens yet.</p>
				}
				{apiTokens !== null && apiTokens.length > 0 &&
				<div className='collection'>
					{apiTokens.map((token) => (
						<div key={token.id} className='collection-item api-token-row'>
							<span>
								<strong className='api-token-name'>{token.name}</strong>
								<br />
								<span className='grey-text'>
									Created {formatTimestamp(token.created_at)}
									{' · '}
									Last used {token.last_used_at ? formatTimestamp(token.last_used_at) : 'Never'}
								</span>
							</span>
							<div className='secondary-content'>
								<Button
									small
									danger
									disabled={revokingId === token.id}
									onClick={() => onRevoke(token)}
								>
									{revokingId === token.id ? 'Revoking…' : 'Revoke'}
								</Button>
							</div>
						</div>
					))}
				</div>
				}
			</Card>
		</div>
	);
}

/** "YYYY-MM-DD HH:MM:SS" from the server, shown to the minute. */
function formatTimestamp(timestamp) {
	if(!timestamp) {
		return '';
	}
	return String(timestamp).slice(0, 16);
}

/**
 * The async clipboard API needs a secure context; without it, fall back to selecting the visible
 * input and the legacy copy command. Resolves to whether anything was copied.
 */
export function copyText(text, inputElement) {
	if(typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
		return navigator.clipboard.writeText(text)
			.then(() => true)
			.catch(() => legacyCopy(inputElement));
	}
	return Promise.resolve(legacyCopy(inputElement));
}

function legacyCopy(inputElement) {
	if(!inputElement || typeof document.execCommand !== 'function') {
		return false;
	}
	try {
		inputElement.focus();
		inputElement.select();
		return !!document.execCommand('copy');
	} catch(error) {
		return false;
	}
}
